import React from "react";
import { LegalPage, LegalSection } from "./LegalPage";

export const Privacy = () => (
  <LegalPage
    eyebrow="Legal · Data & Privacy"
    title="Privacy Policy"
    lastUpdated="February 2026"
  >
    <LegalSection heading="About this policy">
      <p>
        This policy explains how FIND LET (referred to in this policy as "FIND LET", "we", "us" or "our") handles personal information you provide through our website, renter briefs, landlord and property forms, property partner submissions, FIND LET Move enquiries, contact forms and any user account you create.
      </p>
      <p>
        FIND LET is a London rental matching, property introduction and relocation/concierge service. We do not own the properties listed on our platform unless expressly stated.
      </p>
    </LegalSection>

    <LegalSection heading="Information we collect">
      <p>
        We only collect information you voluntarily provide to us. Depending on how you use our service, this may include:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Renter brief details — such as your target boroughs, budget, move-in window, household composition, lifestyle requirements and contact details.</li>
        <li>Landlord and property submissions — such as property specifications, availability, rent, photography, ideal tenant preferences and your contact details.</li>
        <li>Property partner submissions — such as agency name, coverage, integration format and contact details.</li>
        <li>FIND LET Move enquiries — such as service selection, dates, pickup/delivery postcodes and contact details.</li>
        <li>Contact form and message content, along with your name and email address.</li>
        <li>Account information if you register — such as your name, email, role, and hashed password.</li>
        <li>Basic technical information generated when you use our website, for security and troubleshooting.</li>
      </ul>
    </LegalSection>

    <LegalSection heading="How we use your information">
      <p>We use the information you provide to:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Respond to your enquiries and messages.</li>
        <li>Understand your property requirements and prepare curated introductions.</li>
        <li>Facilitate relevant property introductions between renters and landlords or property partners.</li>
        <li>Coordinate viewings and, where you request them, FIND LET Move services.</li>
        <li>Administer your account, the platform and our concierge desk.</li>
        <li>Communicate service updates such as brief confirmations, match notifications and viewing coordination.</li>
        <li>Detect and prevent misuse, fraud or unlawful activity.</li>
        <li>Comply with applicable legal, regulatory or record-keeping obligations.</li>
      </ul>
    </LegalSection>

    <LegalSection heading="Lawful bases">
      <p>Under UK data protection law, we rely on one or more of the following lawful bases:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li><strong>Your consent</strong> — for example, when you send us a brief, enquiry or message.</li>
        <li><strong>Performance of a contract or steps taken at your request</strong> — for example, to prepare property introductions or coordinate a Move you have asked for.</li>
        <li><strong>Legitimate interests</strong> — such as operating the concierge desk, protecting the service from misuse and improving reliability.</li>
        <li><strong>Legal obligation</strong> — where we are required by law to retain or share information.</li>
      </ul>
    </LegalSection>

    <LegalSection heading="Sharing your information">
      <p>
        We do not sell personal information.
      </p>
      <p>
        We share information only where necessary to respond to your request or provide the service you have asked for. In practice, this means:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Relevant renter brief information may be shared with landlords or property partners where necessary to facilitate a property match or introduction you have requested.</li>
        <li>Relevant landlord and property information may be shared with prospective renters where necessary to facilitate a match or viewing.</li>
        <li>Relevant enquiry details may be shared with third-party service providers (for example, removals, cleaning or storage partners) where you have requested a FIND LET Move service.</li>
        <li>Trusted third-party providers who help us operate the platform (such as hosting or infrastructure providers) may process information on our behalf under appropriate confidentiality obligations.</li>
        <li>Regulators, law enforcement or other bodies where we are legally required to disclose information.</li>
      </ul>
    </LegalSection>

    <LegalSection heading="Marketing communications">
      <p>
        Any optional marketing communications from FIND LET will only be sent where you have opted in. You can withdraw your consent at any time by contacting us at{" "}
        <a href="mailto:concierge@findlet.uk" className="editorial-link">concierge@findlet.uk</a>.
      </p>
    </LegalSection>

    <LegalSection heading="Data retention">
      <p>
        We keep personal information for as long as reasonably necessary for the purposes described above, to fulfil the service you have requested, or to comply with applicable legal obligations. When information is no longer needed, we will delete or anonymise it.
      </p>
    </LegalSection>

    <LegalSection heading="Security">
      <p>
        We take reasonable and appropriate technical and organisational measures to protect the personal information you share with us. No online service can be guaranteed to be completely secure, and you share information at your own risk.
      </p>
    </LegalSection>

    <LegalSection heading="International transfers">
      <p>
        Some of the third-party providers we work with may process information outside the United Kingdom. Where this happens, we take reasonable steps to make sure appropriate safeguards are in place as required by UK data protection law.
      </p>
    </LegalSection>

    <LegalSection heading="Your rights">
      <p>Under UK data protection law, you generally have the right to:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Ask for a copy of the personal information we hold about you.</li>
        <li>Ask us to correct inaccurate or incomplete information.</li>
        <li>Ask us to delete your information in certain circumstances.</li>
        <li>Ask us to restrict or object to certain types of processing.</li>
        <li>Ask us to transfer your information to another provider where technically feasible.</li>
        <li>Withdraw consent at any time where we relied on consent.</li>
      </ul>
      <p>
        To exercise any of these rights, please email{" "}
        <a href="mailto:concierge@findlet.uk" className="editorial-link">concierge@findlet.uk</a>.
      </p>
    </LegalSection>

    <LegalSection heading="Complaints">
      <p>
        If you are unhappy with how we have handled your personal information, please contact us first so we can try to resolve the matter. You also have the right to complain to the UK Information Commissioner's Office (ICO) at{" "}
        <a href="https://ico.org.uk" target="_blank" rel="noreferrer" className="editorial-link">ico.org.uk</a>.
      </p>
    </LegalSection>

    <LegalSection heading="Changes to this policy">
      <p>
        We may update this policy from time to time. The "Last updated" date at the top of the page reflects the most recent version.
      </p>
    </LegalSection>

    <LegalSection heading="Contact">
      <p>
        For any questions about this Privacy Policy, please contact the FIND LET concierge desk at{" "}
        <a href="mailto:concierge@findlet.uk" className="editorial-link">concierge@findlet.uk</a>.
      </p>
      <p className="text-xs italic text-[#7096D1]">
        {/* Post-incorporation TODO: insert registered legal operator name, company number and registered office once available. */}
        Full operator details will be published here once FIND LET has completed incorporation.
      </p>
    </LegalSection>
  </LegalPage>
);
